import { CreatePlanContainer } from "@pages/create-plan/create-plan-container/create-plan-container";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { LoadingScreen } from "@components/loading-screen";
import { useAppParams } from "@router/router-helper";
import { useAppNavigation } from "@router/use-app-navigation";
import { RequirePermissionPage } from "@components/common/require-permission-page";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { setPlanSubject } from "@store/plan-creation/plan-creation-slice";
import { useEffect } from "react";
import { fetchCompany } from "@store/workspace/workspace-thunks";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { StringHeader } from "@components/string-header";
import { PlanCreationModeProps } from "@custom-types/base-types";

/** The page title shown for each kind of product the user can add to a workspace */
const PAGE_TITLES: Record<PlanCreationModeProps["creationMode"], string> = {
  subscription: "Create New Workspace Plan",
  feature: "Add Feature to Workspace",
  ["hardware-license"]: "Create New Hardware License",
};

/** Show the general layout of plan creation for workspace */
export function CreateWorkspacePlanPage({
  creationMode,
}: PlanCreationModeProps): React.JSX.Element {
  const { companyId, planId } = useAppParams();
  const { navigateToProfilePage } = useAppNavigation();
  const dispatch = useAppDispatch();

  const { fetchingCompany, company } = useAppSelector(companyStateSelector);

  const { handleErrorWithPage } = useErrorHandler();

  // Fetches the company
  useEffect(() => {
    async function fetchCompanyData(): Promise<void> {
      try {
        await dispatch(fetchCompany({ companyId })).unwrap();
      } catch (error) {
        handleErrorWithPage({
          id: `CreateCompanyPlanPage-${Date.now().toString()}`,
          title: "Could not load workspace",
          error,
        });
      }
    }

    void fetchCompanyData();
  }, [dispatch, companyId, handleErrorWithPage]);

  // Set the plan subject in the store for plan creation
  useEffect(() => {
    if (company) {
      dispatch(
        setPlanSubject({
          planSubjectId: company.id,
          planSubjectType:
            creationMode === "hardware-license" ? "DEVICE" : "COMPANY",
          planSubjectName: company.name,
          planSubscriberId: company.id,
        })
      );
    }
  }, [company, dispatch, creationMode]);

  return (
    <RequirePermissionPage
      permission="workspace.addPlan"
      leaveText="Go to workspace"
      onLeave={() => {
        if (companyId) {
          navigateToProfilePage({
            subjectType: "COMPANY",
            subjectId: companyId,
          });
        }
      }}
    >
      <LoadingScreen
        isFetching={!company || fetchingCompany.selectedCompany === "pending"}
      >
        {company && (
          <ContentLayout
            header={
              <StringHeader
                title={
                  planId ? "Copy Workspace Plan" : PAGE_TITLES[creationMode]
                }
              />
            }
          >
            <CreatePlanContainer creationMode={creationMode} />
          </ContentLayout>
        )}
      </LoadingScreen>
    </RequirePermissionPage>
  );
}
